import { useState } from "react";
import { Crown } from "lucide-react";

const BRAND = { purple: "#a78bfa", pink: "#ec4899" };

// ---------------------------------------------------------------------------
// Theme tokens — mirrors the pattern used in PagedResultsCard so this panel
// can flip between dark and light without touching any layout/logic.
// ---------------------------------------------------------------------------
function getTheme(theme) {
  if (theme === "light") {
    return {
      label: "text-slate-800",
      sub: "text-slate-500",
      subActive: "text-slate-700",
      track: "rgba(120,110,180,0.14)",
      cardBg: "bg-white/70",
      cardBorder: "border-black/5",
      itemBorder: "border-black/10",
      itemBorderDominant: "border-black/20",
      itemBg: "bg-black/[0.02]",
      itemBgDominant: "bg-black/[0.04]",
      itemBgSpotlit: "bg-black/[0.06]",
      itemBorderSpotlit: "border-black/25",
      barTrack: "bg-black/5",
      vsBg: "bg-white",
      vsBorder: "border-black/10",
      vsText: "text-slate-500",
      indexDim: "text-slate-400",
      indexActive: "text-slate-600",
      shadow: "shadow-[0_8px_32px_-16px_rgba(167,139,250,0.25)]",
    };
  }
  return {
    label: "text-white",
    sub: "text-slate-400",
    subActive: "text-slate-200",
    track: "rgba(148,130,255,0.12)",
    cardBg: "bg-white/[0.02]",
    cardBorder: "border-white/5",
    itemBorder: "border-white/5",
    itemBorderDominant: "border-white/15",
    itemBg: "bg-white/[0.015]",
    itemBgDominant: "bg-white/[0.04]",
    itemBgSpotlit: "bg-white/[0.07]",
    itemBorderSpotlit: "border-white/25",
    barTrack: "bg-white/5",
    vsBg: "bg-[#0d0f1a]",
    vsBorder: "border-white/10",
    vsText: "text-slate-400",
    indexDim: "text-slate-500",
    indexActive: "text-slate-300",
    shadow: "shadow-[0_8px_32px_-16px_rgba(167,139,250,0.3)]",
  };
}

// ---------------------------------------------------------------------------
// fields  = uiConfig.fields  -> [{ key, label, color }, ...]  (your existing config)
// values  = testEntry.percentages -> { human: 30, animal: 50, ... }
// counts  = testEntry.counts      -> { human: 3, animal: 5, ... }
// dominant= testEntry.dominant    -> "animal"
// Exactly the same input shape your ChakraZoneBars/DimensionalResultsGrid get,
// just fed from the categorical branch instead of .averages.
// ---------------------------------------------------------------------------
function buildCategories(fields = [], values = {}, counts = {}, dominant) {
  return fields
    .map((f) => {
      const key = f.key ?? f;
      return {
        key,
        label: f.label ?? key,
        pct: Number(values[key]) || 0,
        count: counts[key] ?? 0,
        color: f.color ?? BRAND.purple,
        isDominant: key === dominant,
      };
    })
    .sort((a, b) => b.pct - a.pct);
}

function PremiumDial({ id, pct, color, size = 72, stroke = 6, active, big = false, track }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, pct)) / 100) * c;

  return (
    <div
      className="relative transition-transform duration-300"
      style={{ width: size, height: size, transform: active ? "scale(1.05)" : "scale(1)" }}
    >
      {active && (
        <div
          className="absolute inset-0 -z-10 rounded-full blur-xl transition-opacity duration-300"
          style={{ background: `radial-gradient(circle, ${color}55 0%, transparent 70%)` }}
        />
      )}
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{
          filter: active ? `drop-shadow(0 0 10px ${color}aa)` : `drop-shadow(0 1px 2px rgba(0,0,0,0.1))`,
          transition: "filter 0.3s ease",
          overflow: "visible",
        }}
      >
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 0.9s cubic-bezier(.34,1.56,.64,1)",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={(big ? "text-xl" : "text-xs") + " font-bold tabular-nums"} style={{ color }}>
          {pct}%
        </span>
      </div>
    </div>
  );
}

// Exactly 2 categories: a "duel" layout, dominant one scaled up + crowned.
// Now interactive: hover/tap either side to spotlight it (independent of
// which one is mathematically dominant) and see its response count pop.
function DuoDuel({ categories, active, setActive, t }) {
  const [a, b] = categories;

  return (
    <div className="relative flex items-stretch justify-center gap-4 py-2">
      {[a, b].map((cat, i) => {
        const isSpotlit = active === cat.key;
        const scaleUp = cat.isDominant || isSpotlit;

        return (
          <button
            key={cat.key}
            type="button"
            onMouseEnter={() => setActive(cat.key)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(cat.key)}
            onBlur={() => setActive(null)}
            onClick={() => setActive((p) => (p === cat.key ? null : cat.key))}
            className={
              "relative flex flex-1 max-w-[160px] cursor-pointer flex-col items-center gap-2 rounded-2xl border p-4 text-left transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" +
              (cat.isDominant ? " " + t.itemBorderDominant + " " + t.itemBgDominant : " " + t.itemBorder + " " + t.itemBg) +
              (isSpotlit
                ? " " + t.itemBorderSpotlit + " " + t.itemBgSpotlit + " shadow-[0_8px_24px_-8px_rgba(167,139,250,0.4)]"
                : "") +
              (!isSpotlit && active ? " opacity-60" : " opacity-100")
            }
            style={{
              animation: "fadeUp .5s ease both",
              animationDelay: `${i * 100}ms`,
              transform: scaleUp ? "scale(1.05)" : "scale(1)",
            }}
          >
            {cat.isDominant && (
              <div
                className="absolute -top-3 flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-md"
                style={{
                  background: `linear-gradient(90deg, ${BRAND.purple}, ${BRAND.pink})`,
                  animation: isSpotlit ? "pulseGlow 1.4s ease-in-out infinite" : "none",
                }}
              >
                <Crown size={10} /> Dominant
              </div>
            )}
            <PremiumDial
              id={cat.key}
              pct={cat.pct}
              color={cat.color}
              size={cat.isDominant ? 96 : 78}
              stroke={cat.isDominant ? 9 : 7}
              active={cat.isDominant || isSpotlit}
              big={cat.isDominant}
              track={t.track}
            />
            <span className={"text-center text-[12px] font-semibold leading-tight " + t.label}>{cat.label}</span>
            <span
              className={
                "text-[10px] tabular-nums transition-colors duration-300" +
                (isSpotlit ? " " + t.subActive : " " + t.sub)
              }
            >
              {cat.count} response{cat.count === 1 ? "" : "s"}
            </span>
          </button>
        );
      })}

      <div
        className={
          "pointer-events-none absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-[10px] font-bold shadow-lg transition-transform duration-300 " +
          t.vsBg + " " + t.vsBorder + " " + t.vsText
        }
        style={{ transform: `translate(-50%, -50%) ${active ? "scale(1.15)" : "scale(1)"}` }}
      >
        vs
      </div>
    </div>
  );
}

// 3+ categories: hero dial for the dominant theme + ranked bars underneath.
// Now interactive: hover/tap a row to spotlight it — the hero dial swaps to
// reflect whichever row is active, and rows expand slightly with a shimmer
// sweeping the fill bar.
function RankedBar({ cat, index, active, setActive, t }) {
  const isSpotlit = active === cat.key;

  return (
    <button
      type="button"
      onMouseEnter={() => setActive(cat.key)}
      onMouseLeave={() => setActive(null)}
      onFocus={() => setActive(cat.key)}
      onBlur={() => setActive(null)}
      onClick={() => setActive((p) => (p === cat.key ? null : cat.key))}
      className={
        "flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" +
        (cat.isDominant ? " " + t.itemBorderDominant + " " + t.itemBgDominant : " " + t.itemBorder + " " + t.itemBg) +
        (isSpotlit
          ? " " + t.itemBorderSpotlit + " " + t.itemBgSpotlit + " shadow-[0_6px_20px_-8px_rgba(167,139,250,0.4)] -translate-y-0.5"
          : "")
      }
      style={{ animation: "fadeUp .5s ease both", animationDelay: `${index * 70}ms` }}
    >
      <span
        className={
          "w-5 shrink-0 text-center text-[11px] font-bold transition-colors duration-300" +
          (isSpotlit ? " " + t.indexActive : " " + t.indexDim)
        }
      >
        {index + 1}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className={"truncate text-[12px] font-semibold " + t.label}>{cat.label}</span>
          <span className="shrink-0 text-[11px] font-bold tabular-nums" style={{ color: cat.color }}>
            {cat.pct}%
          </span>
        </div>
        <div className={"relative h-1.5 w-full overflow-hidden rounded-full " + t.barTrack}>
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{ width: `${cat.pct}%`, background: `linear-gradient(90deg, ${cat.color}99, ${cat.color})` }}
          />
          {isSpotlit && (
            <div
              className="absolute inset-y-0 left-0 w-full opacity-60"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
                animation: "shimmer 1.2s ease-in-out infinite",
              }}
            />
          )}
        </div>
        <div
          className={"overflow-hidden text-[10px] tabular-nums transition-all duration-300 ease-out " + t.sub}
          style={{ maxHeight: isSpotlit ? 16 : 0, opacity: isSpotlit ? 1 : 0 }}
        >
          {cat.count} response{cat.count === 1 ? "" : "s"}
        </div>
      </div>
      {cat.isDominant && (
        <Crown
          size={14}
          style={{ color: BRAND.pink }}
          className={"shrink-0 transition-transform duration-300" + (isSpotlit ? " scale-125" : "")}
        />
      )}
    </button>
  );
}

function RankedCategoryList({ categories, active, setActive, t }) {
  const dominant = categories[0];
  const shown = categories.find((c) => c.key === active) ?? dominant;

  return (
    <div className="flex flex-col items-center gap-5 py-1">
      <div className="flex flex-col items-center gap-1">
        <PremiumDial id={shown.key} pct={shown.pct} color={shown.color} size={104} stroke={9} active big track={t.track} />
        <span className={"text-[10px] font-semibold uppercase tracking-wide " + t.sub}>
          {shown.key === dominant.key ? "Dominant Style" : "Highlighted"}
        </span>
        <span className={"text-sm font-bold transition-all duration-300 " + t.label}>{shown.label}</span>
      </div>

      <div className="flex w-full flex-col gap-2">
        {categories.map((cat, i) => (
          <RankedBar key={cat.key} cat={cat} index={i} active={active} setActive={setActive} t={t} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drop-in replacement slot for the categorical branch — same role as
// DimensionalResultsGrid, just fed percentages/counts/dominant instead of averages.
// ---------------------------------------------------------------------------
export default function CategoricalResultsPanel({ fields, values, counts, dominant, theme = "dark" }) {
  const [active, setActive] = useState(null);
  const t = getTheme(theme);
  const categories = buildCategories(fields, values, counts, dominant);
  if (categories.length === 0) return null;

  return (
    <div className={"w-full rounded-3xl border px-3 py-5 backdrop-blur-xl " + t.cardBorder + " " + t.cardBg + " " + t.shadow}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0px rgba(236,72,153,0.4); }
          50% { box-shadow: 0 0 12px rgba(236,72,153,0.7); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      {categories.length === 2 ? (
        <DuoDuel categories={categories} active={active} setActive={setActive} t={t} />
      ) : (
        <RankedCategoryList categories={categories} active={active} setActive={setActive} t={t} />
      )}
    </div>
  );
}